/**
 * Google Address Autocomplete Component
 * Integrates Google Places Autocomplete for address input
 * Uses Material-UI components (no raw HTML elements)
 */

import { useEffect, useRef, useState } from "react";
import { TextField } from "@mui/material";
import { loadGoogleMapsScript } from "@/shared/utils/loadGoogleMaps.js";
import { logWarn, logInfo } from "@/shared/utils/logger.js";
import {
  GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS,
  applySelectedAddressToInput,
  placeToAddressData,
} from "@/shared/utils/googlePlacesAddress.js";

/**
 * Google Address Autocomplete Component
 * @param {Object} props
 * @param {string} props.value - Current address value
 * @param {Function} props.onChange - Callback when address text changes
 * @param {Function} props.onPlaceSelect - Callback when a place is selected from autocomplete
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Input placeholder
 * @param {boolean} props.required - Whether field is required
 * @param {string|Object} props.error - Error message or error object
 * @param {boolean} props.autoFocus - Whether to auto-focus the input
 * @param {Object} props.TextFieldProps - Additional props to pass to TextField
 */
export function GoogleAddressAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  label,
  placeholder = "Street, suburb, mall, or area...",
  required = false,
  error = null,
  autoFocus = false,
  ...TextFieldProps
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [isLoading, setIsLoading] = useState(false);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const isSettingFromPlaceSelectionRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 15;

    const initializeAutocomplete = async () => {
      try {
        await loadGoogleMapsScript();

        if (!isMounted) return;

        if (!window.google?.maps?.places) {
          logWarn("Google Maps Places API not available");
          return;
        }

        if (!inputRef.current) {
          if (retryCount < maxRetries) {
            retryCount += 1;
            setTimeout(() => {
              if (isMounted) {
                initializeAutocomplete();
              }
            }, 150);
            return;
          }
          logWarn("Could not find input element for autocomplete after retries");
          return;
        }

        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current
          );
        }

        const autocomplete = new window.google.maps.places.Autocomplete(
          inputRef.current,
          GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS
        );

        autocomplete.addListener("place_changed", () => {
          const selectedText = inputRef.current?.value || "";
          const place = autocomplete.getPlace();
          const addressData = placeToAddressData(place, selectedText);

          if (!addressData.address) {
            logWarn("No address found for selected place");
            return;
          }

          setIsLoading(true);
          isSettingFromPlaceSelectionRef.current = true;

          setInputValue(addressData.address);
          applySelectedAddressToInput(inputRef.current, addressData.address);
          onChangeRef.current?.(addressData.address);
          onPlaceSelectRef.current?.(addressData);

          requestAnimationFrame(() => {
            applySelectedAddressToInput(inputRef.current, addressData.address);
          });

          setTimeout(() => {
            isSettingFromPlaceSelectionRef.current = false;
          }, 100);
          setIsLoading(false);

          logInfo("Google Address selected", {
            address: addressData.address,
            coordinates: addressData.coordinates,
            addressConfidence: addressData.addressConfidence,
          });
        });

        autocompleteRef.current = autocomplete;
      } catch (initError) {
        logWarn("Failed to initialize Google Address Autocomplete", {
          error: initError.message,
        });
      }
    };

    initializeAutocomplete();

    return () => {
      isMounted = false;
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(
          autocompleteRef.current
        );
      }
    };
  }, []);

  useEffect(() => {
    if (isSettingFromPlaceSelectionRef.current) {
      return;
    }
    setInputValue(value || "");
  }, [value]);

  const handleInputChange = (event) => {
    if (isSettingFromPlaceSelectionRef.current) {
      return;
    }
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
  };

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message || (error ? "Invalid address" : null);

  return (
    <TextField
      inputRef={inputRef}
      label={label}
      value={inputValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      required={required}
      error={!!errorMessage}
      helperText={errorMessage}
      autoFocus={autoFocus}
      disabled={isLoading}
      fullWidth
      {...TextFieldProps}
    />
  );
}
